import { BookingDetailSidePanelProfile } from "PFApp/booking/parts/overview/detail_view/booking_detail/booking_detail_side_panel_context/booking_detail_side_panel_context.types";
import { LoadingDots } from "PFComponents/loading_dots";
import { useProfilesBookings, useProfilesWarnings } from "PFCore/hooks/queries/profile";
import { Profile } from "PFTypes";
import { useTranslation } from "react-i18next";

import { BookingFormDetails, DetailsPanelData } from "../../../details_panel.types";
import { WarningsDetail } from "../../warnings_detail/warnings_detail";
import { WarningsFilter } from "../../warnings_detail/warnings_filter";
import { useOverbookingRanges } from "../use_overbooking_ranges";
import { OverbookingsList } from "./overbookings_list";
import { useOverbookingsFilterOptions } from "./use_overbookings_filter_options";

type OverbookingsDetailProps = {
  profile: Profile | BookingDetailSidePanelProfile | undefined;
  isProfileLoading: boolean;
  data: DetailsPanelData;
  onDeleted?: (entity: any) => Promise<void>;
  onEdited?: () => void;
  goToDate?: (date: Date) => void;
  bookingFormDetails?: BookingFormDetails;
};

export const OverbookingsDetail = ({
  profile,
  isProfileLoading,
  data,
  onDeleted,
  onEdited,
  goToDate,
  bookingFormDetails
}: OverbookingsDetailProps) => {
  const { t } = useTranslation("bookingModule", { keyPrefix: "details.overbookings" });

  const { calendarRange, potentialBookings, filterConfig, potentialOverbookings } = data.overbookingsDetails!; // overbookings side panel needs to have overbookingsDetails provided

  const hasPotentialOverbookings = potentialOverbookings !== undefined;

  const { warningsByProfile, isLoading: isWarningsLoading } = useProfilesWarnings({
    profiles: profile ? [{ id: profile.id }] : [],
    calendarRange,
    enabled: !!profile && !hasPotentialOverbookings
  });
  const overbookings = hasPotentialOverbookings
    ? potentialOverbookings
    : profile
      ? (warningsByProfile?.[profile.id]?.overbookings ?? [])
      : [];

  const rowKey = `profile_${data.id}`;
  const { bookingsByRowKey, isFetching: isLoadingBookings } = useProfilesBookings({
    profiles: profile ? [{ id: profile?.id, rowKey }] : [],
    dateRange: calendarRange,
    enabled: !!profile
  });
  const bookings = [...(bookingsByRowKey[rowKey] ?? []), ...(potentialBookings ?? [])];

  const {
    options,
    selectedOption,
    setSelectedOption,
    isLoading: isOptionsLoading
  } = useOverbookingsFilterOptions({
    filterConfig,
    bookings,
    overbookings
  });

  const { overbookings: selectedOverbookings = [] } =
    options.find(({ option }) => option.id === selectedOption) ?? {};
  const { rangesWithBookings } = useOverbookingRanges({
    overbookings: !selectedOption ? overbookings : selectedOverbookings,
    bookings
  });

  const isLoading =
    !profile || isProfileLoading || isLoadingBookings || isWarningsLoading || isOptionsLoading;

  if (isLoading) {
    return <LoadingDots />;
  }

  const plainOptions = options.map(({ option }) => option);

  return (
    <WarningsDetail
      customHeader={
        !!filterConfig &&
        !!selectedOption && (
          <WarningsFilter
            label={t("bookingsFrom")}
            options={plainOptions}
            selectedOption={selectedOption}
            onChangeSelectedOption={setSelectedOption}
          />
        )
      }
      summary={t("ranges", { count: rangesWithBookings.length })}
    >
      {(selectedOrder) => (
        <OverbookingsList
          rangesWithBookings={rangesWithBookings}
          profile={profile}
          goToDate={goToDate}
          onDeleted={onDeleted}
          onEdited={onEdited}
          selectedSort={selectedOrder}
          data={data}
          bookingFormDetails={bookingFormDetails}
        />
      )}
    </WarningsDetail>
  );
};
